import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, BadgeCheck, Network, Sparkles, BarChart3, Wallet,
  ArrowDownToLine, Receipt, TrendingUp, Zap, Trophy, Banknote, Gem, Award,
  Coins, Bell, FileBarChart, ScrollText, Settings, LogOut, Menu, Hexagon, ShieldCheck, Sun, Moon, LifeBuoy, Layers, Megaphone, ServerCog, Gift, CandlestickChart, Image as ImageIcon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import useDashTheme from "@/lib/useDashTheme";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/virtual-business", label: "Virtual Business", icon: Sparkles },
  { to: "/admin/binary", label: "Binary Tree", icon: Network },
  { to: "/admin/deposits", label: "Deposits", icon: Wallet },
  { to: "/admin/deposit-verify", label: "Deposit Health", icon: ServerCog },
  { to: "/admin/withdrawals", label: "Withdrawals", icon: ArrowDownToLine },
  { to: "/admin/send-fund", label: "Fund Management", icon: Banknote },
  { to: "/admin/global-pools", label: "Global Pool", icon: Layers },
  { to: "/admin/pool-payouts", label: "Pool Payouts", icon: Coins },
  { to: "/admin/news", label: "News", icon: Megaphone },
  { to: "/admin/support", label: "Support", icon: LifeBuoy },
  { to: "/admin/ledger", label: "Income Ledger", icon: Receipt },
  { to: "/admin/config", label: "Program Rules", icon: Settings },
  { to: "/admin/pink-pins", label: "Pink Pins", icon: Gift },
  { to: "/admin/security-center", label: "Security Center", icon: ShieldCheck },
  { to: "/admin/secrets-vault", label: "Secrets Vault", icon: ShieldCheck },
  { to: "/admin/ai-agent", label: "AI Agent", icon: Megaphone },
  { to: "/admin/sign-banner", label: "Sign Banner", icon: ImageIcon },
  { to: "/admin/trading", label: "Trading Control", icon: CandlestickChart },
  { to: "/admin/crypto", label: "Crypto Settings", icon: Coins },
  { to: "/admin/security", label: "Security", icon: ShieldCheck },
  { to: "/admin/marketing", label: "Marketing", icon: Megaphone },
  { to: "/admin/reports", label: "Reports", icon: FileBarChart },
  { to: "/admin/audit", label: "Audit Logs", icon: ScrollText },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [dark, toggleTheme] = useDashTheme();
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  const doLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="grain min-h-screen">
      <aside className={cn(
        "sidebar-crypto fixed inset-y-0 left-0 z-40 w-72 border-r border-white/10 backdrop-blur-xl transition-transform lg:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex h-16 items-center gap-2 border-b border-white/10 px-4">
          <img src="/logo.png" alt="Alexa Fintech" className="h-11 w-auto object-contain" />
          <span className="rounded-full gold-border px-2 py-0.5 text-[10px] uppercase text-[var(--gold)]">Admin</span>
        </div>
        <nav className="flex h-[calc(100vh-4rem)] flex-col gap-1 overflow-y-auto p-4">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end}
              className={({ isActive }) => cn("sidebar-link", isActive && "active")}
              data-testid={`admin-nav-${l.label.toLowerCase().replace(/ /g, "-")}`}>
              <l.icon size={18} /> <span>{l.label}</span>
            </NavLink>
          ))}
          <button onClick={doLogout} className="sidebar-link mt-2 text-rose-300 hover:bg-rose-500/10" data-testid="admin-logout">
            <LogOut size={18} /> Logout
          </button>
        </nav>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-black/60 lg:hidden" onClick={() => setOpen(false)} />}
      <div className={cn("lg:pl-72 bg-[var(--bg)] min-h-screen", dark ? "theme-app theme-dark" : "theme-app")}>
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-white/10 bg-[var(--bg)]/80 px-4 backdrop-blur-xl sm:px-6">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-white" onClick={() => setOpen(true)}><Menu /></button>
            <div className="flex items-center gap-2 text-sm text-[var(--text-dim)]"><ShieldCheck size={16} className="text-[var(--gold)]" /> Control Center</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs text-[var(--text-dim)]">{user?.role?.replace("_", " ")}</p>
              <p className="text-sm font-semibold text-white">{user?.name}</p>
            </div>
            <button onClick={toggleTheme} className="grid h-9 w-9 place-items-center rounded-xl glass-2 text-white" title="Toggle theme" data-testid="admin-theme-toggle">
              {dark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <div className="grid h-9 w-9 place-items-center rounded-xl btn3d-gold text-sm font-bold text-black">{user?.name?.[0]?.toUpperCase()}</div>
          </div>
        </header>
        <main className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
